import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { SubscriptionStatus } from "./models/SubscriptionStatus";
import { SubscriptionPlansResponse } from "./contracts/SubscriptionPlansResponse";
import {
  fetchSubscriptionStatus,
  fetchSubscriptionPlans,
  startSubscriptionCheckout,
  syncCheckout,
} from "./slice-extensions/subscription-slice-extension";

interface SubscriptionSliceState {
  status?: SubscriptionStatus;
  plans?: SubscriptionPlansResponse;
  loadingStatus: LoadingStatus;
  checkoutStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: SubscriptionSliceState = {
  status: undefined,
  plans: undefined,
  loadingStatus: LoadingStatus.Idle,
  checkoutStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearSubscriptionError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionStatus.pending, (state) => {
        state.loadingStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(
        fetchSubscriptionStatus.fulfilled,
        (state, action: PayloadAction<SubscriptionStatus>) => {
          state.loadingStatus = LoadingStatus.Idle;
          state.status = action.payload;
        }
      )
      .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
        state.loadingStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })
      .addCase(
        fetchSubscriptionPlans.fulfilled,
        (state, action: PayloadAction<SubscriptionPlansResponse>) => {
          state.plans = action.payload;
        }
      )
      .addCase(
        syncCheckout.fulfilled,
        (state, action: PayloadAction<SubscriptionStatus>) => {
          state.status = action.payload;
        }
      )
      .addCase(startSubscriptionCheckout.pending, (state) => {
        state.checkoutStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(startSubscriptionCheckout.fulfilled, (state) => {
        state.checkoutStatus = LoadingStatus.Idle;
      })
      .addCase(startSubscriptionCheckout.rejected, (state, action) => {
        state.checkoutStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearSubscriptionError } = subscriptionSlice.actions;

export const selectSubscriptionStatus = (state: RootState) =>
  state.subscription.status;
export const selectSubscriptionPlans = (state: RootState) =>
  state.subscription.plans;
export const selectSubscriptionLoadingStatus = (state: RootState) =>
  state.subscription.loadingStatus;
export const selectSubscriptionCheckoutStatus = (state: RootState) =>
  state.subscription.checkoutStatus;
export const selectSubscriptionError = (state: RootState) =>
  state.subscription.lastError;

export default subscriptionSlice.reducer;
